import React from 'react';
import { AlertTriangle, GitBranch } from 'lucide-react';
import { Button } from '../../../../components/ui';
import type { EmployeeView } from '../../shared';
import { Panel } from './PanelHeader.js';

interface CurrentWorkPanelProps {
  employee: EmployeeView;
  linkedToProject?: boolean;
  onOpenConversation?: () => void;
}

export const CurrentWorkPanel: React.FC<CurrentWorkPanelProps> = ({
  employee,
  linkedToProject = true,
  onOpenConversation,
}) => {
  const blocked = linkedToProject && employee.status === 'blocked';
  const accent = blocked ? 'text-danger' : 'text-accent-text';
  const heading = !linkedToProject
    ? 'No project assignment'
    : employee.currentChange
      ? employee.currentChange.split('·').slice(1).join('·').trim() || employee.currentChange
      : 'Idle — no current task';
  return (
    <Panel className={blocked ? 'border-danger-muted' : ''}>
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5 text-xs">
        <GitBranch size={12} className={accent} />
        <span className={accent}>
          {!linkedToProject ? 'Not linked to this project' : (employee.currentChange ?? 'No active change')}
        </span>
        <span className="text-faint">·</span>
        <span className="text-muted">{linkedToProject ? (employee.workstream ?? '—') : 'Reusable employee'}</span>
        <span className="ml-auto text-faint">last activity {employee.lastActivity}</span>
      </div>
      <div className="px-4 pb-4 pt-3">
        <h3 className="text-lg font-semibold text-heading">{heading}</h3>
        <div className="mt-1 font-mono text-xs text-accent-text">{employee.currentStep}</div>

        {blocked && employee.blockerLong && (
          <div className="mt-3 flex gap-3 rounded-lg border border-danger-muted bg-danger-muted/40 p-3 text-sm">
            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-danger" />
            <div>
              <div className="font-medium text-danger">{employee.blockerShort}</div>
              <div className="mt-1 text-xs text-body">{employee.blockerLong}</div>
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          {!linkedToProject ? (
            <Button size="sm" onClick={onOpenConversation} disabled={!onOpenConversation}>
              Plan assignment
            </Button>
          ) : blocked ? (
            <>
              <Button
                size="sm"
                onClick={onOpenConversation}
                disabled={!onOpenConversation}
                title="Action via conversation"
              >
                Resolve in conversation
              </Button>
              <Button size="sm" variant="ghost" onClick={onOpenConversation} disabled={!onOpenConversation}>
                Discuss blocker
              </Button>
            </>
          ) : employee.status === 'review' ? (
            <>
              <Button size="sm" onClick={onOpenConversation} disabled={!onOpenConversation}>
                Review in conversation
              </Button>
              <Button size="sm" variant="ghost" onClick={onOpenConversation} disabled={!onOpenConversation}>
                Discuss review
              </Button>
            </>
          ) : (
            <Button size="sm" variant="ghost" onClick={onOpenConversation} disabled={!onOpenConversation}>
              Plan next step
            </Button>
          )}
        </div>
      </div>
    </Panel>
  );
};
